/**
 * File: useAddLeadDialogController.ts
 * Path: apps/admin/src/shared/hooks/useAddLeadDialogController.ts
 * Description: Adding a lead by hand: the draft, its checks, the referral partners and saving.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ADMIN_PATHS } from "@idk/config";
import { toast } from "@idk/ui";
import { useAffiliates } from "./useAffiliatesQueries";
import { useLeadMutations } from "./useLeadsMutations";
import {
  EMPTY_ADD_LEAD,
  toLeadDetails,
  validateAddLead,
  type AddLeadDraft,
  type AddLeadErrors,
} from "../utils/addLeadDraftUtils";

export type AddLeadTextKey = Exclude<keyof AddLeadDraft, "source" | "affiliateId" | "marketingConsent">;

export const useAddLeadDialogController = (open: boolean, onOpenChange: (open: boolean) => void) => {
  const navigate = useNavigate();
  const { create } = useLeadMutations();
  const [draft, setDraft] = useState<AddLeadDraft>(EMPTY_ADD_LEAD);
  const [errors, setErrors] = useState<AddLeadErrors>({});
  const partners = useAffiliates(open && draft.source === "referral");

  const set = <K extends keyof AddLeadDraft>(key: K, value: AddLeadDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const text = (key: AddLeadTextKey) => (value: string) => set(key, value);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDraft(EMPTY_ADD_LEAD);
      setErrors({});
    }
    onOpenChange(next);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validateAddLead(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    try {
      const id = await create.mutateAsync({
        details: toLeadDetails(draft),
        origin: { source: draft.source, affiliateId: draft.affiliateId || null },
      });
      toast.success(`${draft.name.trim()} added to the pipeline.`);
      handleOpenChange(false);
      navigate(ADMIN_PATHS.businesses.detail(id));
    } catch {
      // The mutation already showed the error; the form stays open to retry.
    }
  };

  return { draft, errors, set, text, partners, pending: create.isPending, handleOpenChange, handleSubmit };
};
